/**
 * RetryPolicyRenderer - Minimal retry policy configuration
 *
 * Clean design: label + dropdown, expandable details only when needed
 */

import { useState, useCallback } from 'react';
import { makeStyles, tokens, Text, Select, Input, Field } from '@coreai/fluentui-react';
import type { RetryPolicy, RetryPolicyType } from '@/types/nodeSettings';
import { DEFAULT_RETRY_POLICY, completeRetryPolicy } from '@/types/nodeSettings';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';
import { validateDuration, validateRetryCount } from '@/utils/validation';

const useStyles = makeStyles({
  container: {
    // Card container - no border needed
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    minHeight: '44px',
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  typeSelect: {
    minWidth: '140px',
  },
  // Expanded fields shown when type is fixed or exponential
  fieldsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
});

const RETRY_TYPES: { value: RetryPolicyType; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'none', label: 'None' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'exponential', label: 'Exponential' },
];

export interface RetryPolicyRendererProps extends SettingRendererProps<RetryPolicy> {
  label?: string;
}

/**
 * RetryPolicyRenderer component for retry policy configuration
 */
export const RetryPolicyRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Retry Policy',
}: RetryPolicyRendererProps) => {
  const styles = useStyles();
  const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});

  const currentPolicy = setting.value ?? DEFAULT_RETRY_POLICY;

  const handleTypeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = event.target.value as RetryPolicyType;
    setValidationErrors({});
    // completeRetryPolicy() preserves any fields the user had set and backfills
    // the rest from DEFAULT_RETRY_POLICY, so switching to 'fixed'/'exponential'
    // never produces a policy missing the runtime-required Count/Interval.
    onChange(completeRetryPolicy({ ...currentPolicy, type: newType }));
  }, [currentPolicy, onChange]);

  const handleCountChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const numValue = parseInt(rawValue, 10);

    // Validate
    const validation = validateRetryCount(isNaN(numValue) ? undefined : numValue);
    setValidationErrors((prev) => ({ ...prev, count: validation.error ?? null }));

    // Build new policy, only including count if it's a valid number
    const newPolicy = { ...currentPolicy };
    if (!isNaN(numValue)) {
      newPolicy.count = numValue;
    } else {
      delete newPolicy.count;
    }
    onChange(newPolicy);
  }, [currentPolicy, onChange]);

  const handleDurationChange = useCallback((field: 'interval' | 'minimumInterval' | 'maximumInterval') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    // Validate duration
    const validation = validateDuration(value);
    setValidationErrors((prev) => ({ ...prev, [field]: validation.error ?? null }));

    // Build new policy, only including field if it has a value
    const newPolicy = { ...currentPolicy };
    if (value) {
      newPolicy[field] = value;
    } else {
      delete newPolicy[field];
    }
    onChange(newPolicy);
  }, [currentPolicy, onChange]);

  // Don't render if setting is not supported
  if (!setting.isSupported) {
    return null;
  }

  const showAdvancedFields = currentPolicy.type === 'fixed' || currentPolicy.type === 'exponential';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.label}>{label}</Text>
        <Select
          className={styles.typeSelect}
          value={currentPolicy.type}
          onChange={handleTypeChange}
          disabled={readOnly}
          aria-label="Retry policy type"
        >
          {RETRY_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </Select>
      </div>

      {/* Advanced Fields (for fixed/exponential) */}
      {showAdvancedFields && (
        <div className={styles.fieldsContainer}>
          <div className={styles.fieldWrapper}>
            <Field
              label="Count"
              size="small"
              {...(validationErrors.count ? { validationState: 'error' as const, validationMessage: validationErrors.count } : {})}
            >
              <Input
                type="number"
                min={1}
                max={90}
                // Show actual state (not a phantom default) so a cleared field
                // doesn't look populated. Validation will surface a missing
                // required field instead of letting it silently serialize.
                value={currentPolicy.count !== undefined ? String(currentPolicy.count) : ''}
                onChange={handleCountChange}
                disabled={readOnly}
              />
            </Field>
          </div>

          <div className={styles.fieldWrapper}>
            <Field
              label="Interval"
              size="small"
              {...(validationErrors.interval ? { validationState: 'error' as const, validationMessage: validationErrors.interval } : {})}
            >
              <Input
                value={currentPolicy.interval ?? ''}
                onChange={handleDurationChange('interval')}
                placeholder="PT7S"
                disabled={readOnly}
              />
            </Field>
          </div>

          {currentPolicy.type === 'exponential' && (
            <>
              <div className={styles.fieldWrapper}>
                <Field
                  label="Min Interval"
                  size="small"
                  {...(validationErrors.minimumInterval ? { validationState: 'error' as const, validationMessage: validationErrors.minimumInterval } : {})}
                >
                  <Input
                    value={currentPolicy.minimumInterval ?? ''}
                    onChange={handleDurationChange('minimumInterval')}
                    placeholder="PT5S"
                    disabled={readOnly}
                  />
                </Field>
              </div>

              <div className={styles.fieldWrapper}>
                <Field
                  label="Max Interval"
                  size="small"
                  {...(validationErrors.maximumInterval ? { validationState: 'error' as const, validationMessage: validationErrors.maximumInterval } : {})}
                >
                  <Input
                    value={currentPolicy.maximumInterval ?? ''}
                    onChange={handleDurationChange('maximumInterval')}
                    placeholder="PT1H"
                    disabled={readOnly}
                  />
                </Field>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

RetryPolicyRenderer.displayName = 'RetryPolicyRenderer';

// Register this renderer
registerSettingRenderer('retryPolicy', RetryPolicyRenderer);
